// The page's view of the extension: it holds no key material or base URLs, and only renders the connection lights.

import { type BridgeResponse, callBridge, pingExtension, waitForBridge } from './bridge';
import manifest from '../../extension/manifest.json';

// Chrome loads the extension from disk, so an updated page routinely meets an older worker; the chip flags it.
export const REQUIRED_EXTENSION_VERSION: string = manifest.version;

/** True when `installed` parses as a version older than the page requires. */
export function extensionOutdated(installed: string | null): boolean {
  if (!installed) return false;
  const parse = (v: string) => v.split('.').map((n) => parseInt(n, 10));
  const have = parse(installed);
  const need = parse(REQUIRED_EXTENSION_VERSION);
  if (have.length < 3 || have.some((n) => Number.isNaN(n))) return false; // unparseable: no opinion
  for (let i = 0; i < 3; i++) {
    if (have[i] !== need[i]) return have[i] < need[i];
  }
  return false;
}

/** checking: key loaded, nothing has called yet. unset: no key. */
export type VenueState = 'connected' | 'disconnected' | 'checking' | 'unset';

export interface VenueStatus {
  id: string;
  label: string;
  hasKey: boolean;
  state: VenueState;
  /** What the extension saw last, e.g. "HTTP 401 — signature rejected". */
  detail: string;
  env: string | null;
  wsUrl: string | null;
  /** The venue's authenticated account stream, when it has one. */
  privateWsUrl: string | null;
  keyIdPrefix: string | null;
}

/** Order matters: it is the order the sidebar and the chip list venues in. */
export const KNOWN_VENUES: { id: string; label: string }[] = [
  { id: 'kalshi', label: 'Kalshi' },
  { id: 'polymarket', label: 'Polymarket US' },
];

export interface ConnectionStatus {
  /** The content script answered — the extension is installed and enabled here. */
  bridged: boolean;
  /** The service worker answered — it is alive and this origin is not blocked. */
  workerReachable: boolean;
  /** This origin has been approved in the popup. */
  authorized: boolean;
  /** A Kalshi key has been loaded in the popup. */
  hasKey: boolean;
  env: 'prod' | 'demo' | null;
  wsUrl: string | null;
  /** The popup's trading kill-switch. */
  trading: boolean;
  keyIdPrefix: string | null;
  version: string | null;
  error: string | null;
  /** Every venue the extension knows, in KNOWN_VENUES order, extras after. */
  venues: VenueStatus[];
}

function blankVenue(id: string, label: string): VenueStatus {
  return { id, label, hasKey: false, state: 'unset', detail: '', env: null, wsUrl: null, privateWsUrl: null, keyIdPrefix: null };
}

function blankVenues(): VenueStatus[] {
  return KNOWN_VENUES.map((v) => blankVenue(v.id, v.label));
}

const BLANK: Omit<ConnectionStatus, 'venues'> = {
  bridged: false, workerReachable: false, authorized: false, hasKey: false,
  env: null, wsUrl: null, trading: false, keyIdPrefix: null, version: null, error: null,
};

const STATES: VenueState[] = ['connected', 'disconnected', 'checking', 'unset'];

function normState(v: unknown): VenueState {
  return STATES.includes(v as VenueState) ? (v as VenueState) : 'unset';
}

// A pre-1.4 worker sends no `venues` at all, only Kalshi's hasKey — handled as a degraded single-venue reply.
function parseVenues(s: BridgeResponse): VenueStatus[] {
  const raw = (s.venues && typeof s.venues === 'object' ? s.venues : null) as Record<string, any> | null;
  const known = KNOWN_VENUES.map(({ id, label }) => {
    const r = raw?.[id];
    if (r) {
      return {
        id,
        label: String(r.label ?? label),
        hasKey: Boolean(r.hasKey),
        state: normState(r.state),
        detail: String(r.detail ?? ''),
        env: (r.env as string | null) ?? null,
        wsUrl: (r.wsUrl as string | null) ?? null,
        privateWsUrl: (r.privateWsUrl as string | null) ?? null,
        keyIdPrefix: (r.keyIdPrefix as string | null) ?? null,
      };
    }
    if (id === 'kalshi' && !raw) {
      return {
        ...blankVenue(id, label),
        hasKey: Boolean(s.hasKey),
        state: s.hasKey ? 'checking' as VenueState : 'unset' as VenueState,
        env: (s.env as string | null) ?? null,
        wsUrl: (s.wsUrl as string | null) ?? null,
        keyIdPrefix: (s.keyIdPrefix as string | null) ?? null,
      };
    }
    return blankVenue(id, label);
  });
  const extras = raw
    ? Object.keys(raw).filter((id) => !KNOWN_VENUES.some((k) => k.id === id))
      .map((id) => ({ ...blankVenue(id, String(raw[id]?.label ?? id)), hasKey: Boolean(raw[id]?.hasKey), state: normState(raw[id]?.state) }))
    : [];
  return [...known, ...extras];
}

export async function getConnectionStatus(): Promise<ConnectionStatus> {
  const bridged = await waitForBridge(2500);
  if (!bridged) return { ...BLANK, venues: blankVenues() };

  // PING is the one call the worker answers from any origin: "extension missing" vs. "present but not approved".
  const info = await pingExtension();
  if (!info) return { ...BLANK, bridged: true, venues: blankVenues() };

  // Anything else from an unapproved origin is a guaranteed error; PING already gave us all it would say.
  if (!info.authorized) {
    return { ...BLANK, bridged: true, workerReachable: true, version: info.version, venues: blankVenues() };
  }

  const s = await callBridge({ type: 'STATUS' }, 6_000);
  if (!s.ok) {
    return { ...BLANK, bridged: true, workerReachable: true, version: info.version, error: String(s.error), venues: blankVenues() };
  }
  return {
    bridged: true,
    workerReachable: true,
    authorized: true,
    hasKey: Boolean(s.hasKey),
    env: (s.env as 'prod' | 'demo' | null) ?? null,
    wsUrl: (s.wsUrl as string | null) ?? null,
    trading: Boolean(s.trading),
    keyIdPrefix: (s.keyIdPrefix as string | null) ?? null,
    version: info.version,
    error: null,
    venues: parseVenues(s),
  };
}

/** True once the extension is installed, this origin approved, and a Kalshi key loaded. */
export function isReady(s: ConnectionStatus): boolean {
  return s.bridged && s.workerReachable && s.authorized && s.hasKey && !!s.wsUrl;
}
